import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    url: String,
    initialState: { type: String, default: 'start' },
    expiresAt: Number,
    copiedLabel: String,
    copyLabel: String
  }

  static targets = ['state', 'copySource', 'copyLabel', 'expiresLabel', 'maskedPhone']

  connect() {
    const state = ['failed', 'cancelled'].includes(this.initialStateValue) ? 'expired' : this.initialStateValue
    this.showState(state)
    if (state === 'waiting') this.beginWaiting()
  }

  disconnect() {
    this.stopWaiting()
    window.clearTimeout(this.copyTimer)
  }

  start() {
    this.showState('waiting')
    this.beginWaiting()
  }

  cancel() {
    this.stopWaiting()
    this.showState('start')
  }

  async copy() {
    if (!this.hasCopySourceTarget) return

    const message = this.copySourceTarget.textContent.trim()
    try {
      await navigator.clipboard.writeText(message)
    } catch {
      const input = document.createElement('textarea')
      input.value = message
      input.setAttribute('readonly', '')
      input.style.position = 'fixed'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      input.remove()
    }

    if (!this.hasCopyLabelTarget) return

    this.copyLabelTarget.textContent = this.copiedLabelValue
    window.clearTimeout(this.copyTimer)
    this.copyTimer = window.setTimeout(() => {
      if (this.hasCopyLabelTarget) this.copyLabelTarget.textContent = this.copyLabelValue
    }, 1600)
  }

  beginWaiting() {
    this.stopWaiting()
    this.updateExpiry()
    this.countdownTimer = window.setInterval(() => this.updateExpiry(), 1000)
    this.poll()
  }

  stopWaiting() {
    window.clearTimeout(this.pollTimer)
    window.clearInterval(this.countdownTimer)
  }

  async poll() {
    if (!this.urlValue || this.currentState !== 'waiting') return

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      })
      if (!response.ok) return this.schedulePoll()

      const status = await response.json()
      if (status.state === 'verified') {
        if (this.hasMaskedPhoneTarget) this.maskedPhoneTarget.textContent = status.masked_phone || ''
        this.stopWaiting()
        this.showState('verified')
        return
      }
      if (['failed', 'expired', 'cancelled'].includes(status.state)) {
        this.stopWaiting()
        this.showState('expired')
        return
      }
    } catch {
      // Mobile networks often pause requests while WhatsApp is in the foreground.
    }

    this.schedulePoll()
  }

  schedulePoll() {
    if (this.currentState !== 'waiting') return
    this.pollTimer = window.setTimeout(() => this.poll(), 2000)
  }

  updateExpiry() {
    if (!this.hasExpiresAtValue) return

    const seconds = Math.max(0, Math.ceil((this.expiresAtValue - Date.now()) / 1000))
    if (this.hasExpiresLabelTarget) {
      this.expiresLabelTarget.textContent = seconds >= 60 ? `${Math.ceil(seconds / 60)} min` : `${seconds} s`
    }

    if (seconds === 0 && this.currentState === 'waiting') {
      this.stopWaiting()
      this.showState('expired')
    }
  }

  showState(state) {
    this.currentState = state
    this.stateTargets.forEach((panel) => {
      const active = panel.dataset.state === state
      panel.hidden = !active
      if (active) {
        panel.classList.remove('is-entering')
        void panel.offsetWidth
        panel.classList.add('is-entering')
      }
    })
  }
}
